import { Switch } from "@/components/ui/switch";
import { fromLines, toLines } from "@/lib/format";

export const PageHead = ({ title, eyebrow, children }) => (
  <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 pb-6 mb-8 border-b border-[#27272A]">
    <div>
      {eyebrow && <p className="eb-label mb-2">{eyebrow}</p>}
      <h1 className="text-3xl md:text-4xl font-bold tracking-tight" data-testid="admin-page-title">{title}</h1>
    </div>
    {children && <div className="flex flex-wrap gap-2">{children}</div>}
  </div>
);

export const F = ({ label, hint, children, className = "" }) => (
  <label className={`block space-y-1.5 ${className}`}>
    <span className="block text-xs uppercase tracking-[0.15em] text-zinc-500">{label}</span>
    {children}
    {hint && <span className="block text-xs text-zinc-600">{hint}</span>}
  </label>
);

export const In = ({ value, onChange, testid, ...rest }) => (
  <input className="eb-input !py-2.5" value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} {...rest} />
);

export const Num = ({ value, onChange, testid, ...rest }) => (
  <input type="number" className="eb-input !py-2.5" value={value ?? ""} data-testid={testid}
    onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} {...rest} />
);

export const Area = ({ value, onChange, rows = 3, testid, ...rest }) => (
  <textarea className="eb-input !py-2.5 resize-y" rows={rows} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} {...rest} />
);

export const Lines = ({ value, onChange, rows = 3, testid, placeholder = "One per line" }) => (
  <textarea className="eb-input !py-2.5 resize-y" rows={rows} placeholder={placeholder} defaultValue={toLines(value)}
    onBlur={(e) => onChange(fromLines(e.target.value))} data-testid={testid} />
);

export const Sel = ({ value, onChange, options, testid, placeholder }) => (
  <select className="eb-input !py-2.5" value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
    {placeholder !== undefined && <option value="">{placeholder}</option>}
    {options.map((o) => (typeof o === "string" ? <option key={o} value={o}>{o}</option> : <option key={o.value} value={o.value}>{o.label}</option>))}
  </select>
);

export const Toggle = ({ label, checked, onChange, testid }) => (
  <label className="flex items-center justify-between gap-4 py-2 cursor-pointer">
    <span className="text-sm text-zinc-300">{label}</span>
    <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testid} className="data-[state=checked]:bg-[#0055FF]" />
  </label>
);

const TONES = {
  New: "border-[#00E5FF]/50 text-[#00E5FF]", Reviewing: "border-blue-400/50 text-blue-300", "Awaiting Information": "border-amber-400/50 text-amber-300",
  "Quote Sent": "border-violet-400/50 text-violet-300", "Awaiting Payment": "border-amber-400/50 text-amber-300", "awaiting payment": "border-amber-400/50 text-amber-300", paid: "border-emerald-400/50 text-emerald-300", Approved: "border-emerald-400/50 text-emerald-300", Declined: "border-red-400/50 text-red-300",
  "Converted to Project": "border-zinc-500 text-zinc-300", Completed: "border-emerald-400/50 text-emerald-300", Cancelled: "border-red-400/50 text-red-300",
  "On Hold": "border-amber-400/50 text-amber-300", "In Progress": "border-[#0055FF] text-[#6f9bff]", sent: "border-emerald-400/50 text-emerald-300",
  failed: "border-red-400/50 text-red-300", draft: "border-zinc-500 text-zinc-300", accepted: "border-emerald-400/50 text-emerald-300",
  declined: "border-red-400/50 text-red-300", expired: "border-amber-400/50 text-amber-300", skipped: "border-zinc-600 text-zinc-500",
};

export const Pill = ({ status, testid }) => (
  <span className={`inline-block whitespace-nowrap border px-2 py-0.5 text-[11px] uppercase tracking-wider ${TONES[status] || "border-zinc-600 text-zinc-300"}`} data-testid={testid}>{status}</span>
);

export const Panel = ({ title, children, action, className = "", testid }) => (
  <section className={`border border-[#27272A] bg-[#0a0a0a] ${className}`} data-testid={testid}>
    {title && (
      <div className="flex items-center justify-between px-5 py-3 border-b border-[#27272A]">
        <h2 className="text-xs uppercase tracking-[0.2em] text-zinc-400 font-semibold font-body">{title}</h2>{action}
      </div>
    )}
    <div className="p-5">{children}</div>
  </section>
);

export const KV = ({ k, v, testid }) => (
  <div className="grid grid-cols-3 gap-3 py-2 border-b border-[#27272A]/60 last:border-0 text-sm">
    <dt className="text-zinc-500">{k}</dt><dd className="col-span-2 text-zinc-200 break-words whitespace-pre-line" data-testid={testid}>{v || "—"}</dd>
  </div>
);

export const Btn = ({ children, variant = "primary", className = "", ...rest }) => (
  <button className={`${variant === "primary" ? "eb-btn-primary" : variant === "danger" ? "eb-btn border border-red-500/50 text-red-300 hover:bg-red-500/10" : "eb-btn-ghost"} !px-4 !py-2 ${className}`} {...rest}>{children}</button>
);
